/* eslint-disable no-param-reassign */

import * as THREE from 'three';
import { TimelineMax, TweenMax, Expo } from 'gsap/TweenMax';

import _debounce from 'lodash/debounce';
import DLAnimate from 'dl-animate/dist/dl-animate-module';
import isMobile from '@/scripts/utils/detect-mobile';
import isIe from '@/scripts/utils/detect-ie';
import isSafari from '@/scripts/utils/detect-safari';
import selectors from '@/scripts/animation/animation-selectors';
import createEvent from '@/scripts/utils/create-event';
import { DOCUMENT_ELEMENT, KEY_CODES } from '@/scripts/constants';
import Hammer from 'hammerjs';
import './CoverSlider.scss';
import fragment from './fragment.glsl';
import vertex from './vertex.glsl';
import { videos, loadImages, renderVideo, playVideo, stopVideo, loadVideo, getUvRate } from './helper';

const defaults = {
  element: null,
  sceneContainer: '[data-role="cover-slider__scene"]',
  slide: '[data-role="cover-slider__slide"]',
  content: '[data-role="cover-slider__content"]',
};

class CoverSlider {
  constructor(options) {
    this.element = options.element;
    this.sceneContainer = this.element.querySelector(options.sceneContainer);
    this.content = this.element.querySelector(options.content);
    this.gallery = options.gallery;

    this.meshUVRate = {
      x: 1.6,
      y: 1,
    };

    this.dpi = window.devicePixelRatio;
    this.vw = $(this.element).width();
    this.vh = $(this.element).height();

    this.camera = new THREE.PerspectiveCamera(
      70,
      this.vw / this.vh,
      0.001, 100,
    );
    this.nav = [];
    this.navDots = [];
    this.curslide = 0;
    this.nextslide = 1;
    this.scene = new THREE.Scene();
    this.renderer = new THREE.WebGLRenderer();
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        dpi: { type: 'f', value: this.dpi },
        uPixels: { type: 'v2', value: new THREE.Vector2(this.vw, this.vh) },
        uPlaneUVRate: {
          type: 'v2',
          value: new THREE.Vector2(this.meshUVRate.x, this.meshUVRate.y),
        },
        uTexture1UVRate: {
          type: 'v2',
          value: getUvRate(this.gallery[this.curslide].image.width, this.gallery[this.curslide].image.height),
        },
        uTexture1: {
          value: this.gallery[this.curslide].texture,
        },
        uTexture2UVRate: {
          type: 'v2',
          value: getUvRate(this.gallery[this.curslide].image.width, this.gallery[this.curslide].image.height),
        },
        uTexture2: {
          value: this.gallery[this.curslide].texture,
        },
        mouse: { type: 'v2', value: new THREE.Vector2(0, 0) },
        time: { type: 'f', value: 0 },
        velocity: { type: 'f', value: 0 },
        accel: { type: 'v2', value: new THREE.Vector2(0.5, 2) },
        progress: { type: 'f', value: 0 },
        doWaves: { type: 'b', value: true },
      },
      vertexShader: vertex,
      fragmentShader: fragment,
    });
    this.plane = new THREE.Mesh(new THREE.PlaneGeometry(this.meshUVRate.x, this.meshUVRate.y, 1, 1), this.material);
    this.duration = 0.3;
    this.timeout = null;


    this.destination = { x: 0, y: 0 };
    this.mouse = { x: 0, y: 0 };
    this.targetPosition = new THREE.Vector3();

    this.activeSlide = 0;
    this.position = 0;
    this.animate = new DLAnimate();
    this.slideChaging = false;
    this.playing = false;
    this.time = 0;


    this.render = this.render.bind(this);
    this.start = this.start.bind(this);
    this.stop = this.stop.bind(this);

    loadVideo(this.gallery[this.activeSlide].image).then(() => {
      this.init();
    });
  }

  init() {
    this.loadNearestVideos();
    this.initEvents();

    this.gallery.forEach((element) => {
      const { $button } = element;
      if ($button) {
        $(this.content).append($button);
      }
    });

    this.element.classList.add('cover-slider');

    // this.renderer.setPixelRatio(this.dpi);
    this.renderer.setSize(this.vw, this.vh);

    this.sceneContainer.appendChild(this.renderer.domElement);
    this.camera.position.set(0, 0, 1);

    this.scene.add(this.plane);

    TweenMax.ticker.addEventListener('tick', this.render);

    this.resize();
    if (this.gallery.length > 1) {
      this.initDots();
      this.initNav();
    }


    window.addEventListener('resize', _debounce(() => this.resize(), 100));
    if (isMobile()) {
      this.element.addEventListener('touchmove', this.onTouchMove.bind(this));
    } else {
      this.element.addEventListener('mousemove', this.onMouseMove.bind(this));
    }
    this.prepareUniforms(this.activeSlide);
    this.animateUniforms(() => {
      this.material.uniforms.progress.value = 0;
      this.showButton();
      this.material.uniforms.doWaves.value = !(isSafari() && $(this.gallery[this.curslide].image).is('video'));
      this.material.uniforms.uTexture1UVRate.value = this.material.uniforms.uTexture2UVRate.value;
      this.material.uniforms.uTexture1.value = this.material.uniforms.uTexture2.value;
      this.material.needsUpdate = true;
    });
  }

  loadNearestVideos() {
    const { prev, next } = this.getIndices();
    loadVideo(this.gallery[prev].image);
    loadVideo(this.gallery[next].image);
  }

  initEvents() {
    const { section, sectionEventStart, sectionEventStop } = selectors;
    const container = this.element.closest(section);

    container.addEventListener(sectionEventStart, this.start);
    container.addEventListener(sectionEventStop, this.stop);
  }

  resize() {
    this.vw = $(this.element).width();
    this.vh = $(this.element).height();

    this.renderer.setSize(this.vw, this.vh);
    this.camera.aspect = this.vw / this.vh;

    this.material.uniforms.uPixels.value.x = this.vw;
    this.material.uniforms.uPixels.value.y = this.vh;

    const aspect = this.meshUVRate.x / this.meshUVRate.y;

    // calculate scene
    const dist = this.camera.position.z - this.plane.position.z;
    const height = 1;
    this.camera.fov = 2 * (180 / Math.PI) * Math.atan(height / (2 * dist));

    if (aspect < this.camera.aspect) {
      this.plane.scale.x = this.plane.scale.y = this.vw / this.vh / aspect; // eslint-disable-line no-multi-assign
    } else {
      this.plane.scale.x = this.plane.scale.y = 1; // eslint-disable-line no-multi-assign
    }

    this.camera.updateProjectionMatrix();
  }

  initDots() {
    const $navDotsContainer = $('<ul class="slider-nav"></ul>');
    for (let i = 0; i < this.gallery.length; i += 1) {
      const $navDot = $(`<li data-main-animation="fade-in-up"><span class="slider-nav__dot ${i === this.activeSlide ? 'is-active' : ''}" data-role="nav-dot" data-index="${i}"></span></li>`);
      $navDot.on('click', () => {
        if (this.slideChaging) {
          return false;
        }
        return this.changeSlide({ newIndex: i, nav: null });
      });
      $navDotsContainer.append($navDot);
      TweenMax.set($navDot, { autoAlpha: 0 });
    }

    this.navDots = $navDotsContainer.find('[data-role="nav-dot"]');

    $(this.content).append($navDotsContainer);
  }

  initNav() {
    const $navNext = $('<div class="nav nav_next cover-slider__nav cover-slider__nav_next" data-direction="next"></div>');
    const $navPrev = $('<div class="nav nav_prev cover-slider__nav cover-slider__nav_prev" data-direction="prev"></div>');

    [$navNext, $navPrev].forEach(($nav) => {
      $nav.on('click', () => this.moveSlider({ direction: $nav.data('direction') }));
    });

    $(this.content).append([$navNext, $navPrev]);

    this.initKeyboardNav();
    if (isMobile()) {
      this.initTouchNav();
    }
  }

  initKeyboardNav() {
    document.addEventListener('keyup', (e) => {
      if (e.keyCode === KEY_CODES.ARROW_LEFT) this.moveSlider({ direction: 'prev' });
      if (e.keyCode === KEY_CODES.ARROW_RIGHT) this.moveSlider({ direction: 'next' });
    });
  }

  initTouchNav() {
    const hammer = new Hammer(this.element);
    hammer.on('swipeleft', () => this.moveSlider({ direction: 'prev' }));
    hammer.on('swiperight', () => this.moveSlider({ direction: 'next' }));
  }


  moveSlider({ direction }) {
    if (this.slideChaging || !this.playing) return;
    const { prev, next } = this.getIndices();
    const newIndex = direction === 'prev' ? prev : next;
    this.changeSlide({ newIndex });
  }

  onTouchMove(e) {
    const target = e.touches[0];
    this.mouse.x = target.screenX;
    this.mouse.y = target.screenY;
  }

  onMouseMove(e) {
    this.mouse.x = e.pageX;
    this.mouse.y = e.pageY;
  }

  changeSlide({ newIndex }) {
    this.prepareUniforms(newIndex);

    playVideo(this.gallery[newIndex].image);

    this.slideChaging = true;
    this.navDots[this.activeSlide].classList.remove('is-active');
    this.navDots[newIndex].classList.add('is-active');
    this.hideButton();

    this.animateUniforms(() => {
      this.material.uniforms.progress.value = 0;
      this.curslide = newIndex;
      this.showButton();
      this.material.uniforms.doWaves.value = !(isSafari() && $(this.gallery[this.curslide].image).is('video'));

      this.material.uniforms.uTexture1UVRate.value = this.material.uniforms.uTexture2UVRate.value;
      this.material.uniforms.uTexture1.value = this.material.uniforms.uTexture2.value;

      this.material.needsUpdate = true;

      stopVideo(this.gallery[this.activeSlide].image);
      this.activeSlide = newIndex;
      this.loadNearestVideos();
      this.slideChaging = false;
      this.element.dispatchEvent(createEvent('slideChanged'));
      this.play();
    });
  }

  prepareUniforms(newIndex) {
    const imageOrVideo = this.gallery[newIndex].image;
    const isVideo = $(imageOrVideo).is('video');

    this.material.uniforms.uTexture2.value = this.gallery[newIndex].texture;
    this.material.uniforms.uTexture2UVRate.value = isVideo
      ? getUvRate(imageOrVideo.videoWidth, imageOrVideo.videoHeight)
      : getUvRate(imageOrVideo.width, imageOrVideo.height);
  }

  animateUniforms(onComplete) {
    const tl = new TimelineMax({
      onComplete,
    });
    tl
      .to(this.material.uniforms.uTexture1UVRate.value, this.duration * 8, {
        x: this.material.uniforms.uTexture2UVRate.value.x,
        y: this.material.uniforms.uTexture2UVRate.value.y,
        easing: Expo.easeInOut,
      })
      .to(this.material.uniforms.progress, this.duration * 8, {
        value: 1,
        easing: Expo.easeInOut,
      }, 0);
  }

  calculateMouse() {
    this.material.uniforms.mouse.value.x += (this.mouse.x / this.vw - this.material.uniforms.mouse.value.x) * 0.05;
    this.material.uniforms.mouse.value.y += (this.mouse.y / this.vh - this.material.uniforms.mouse.value.y) * 0.05;

    let speed = Math.sqrt((this.mouse.x - this.destination.x) ** 2 + (this.mouse.y - this.destination.y) ** 2);

    speed = Math.min(speed, 20);

    this.material.uniforms.velocity.value = speed / 20;

    this.destination.x = this.mouse.x;
    this.destination.y = this.mouse.y;
  }

  render() {
    this.calculateMouse();

    this.time += 0.05;
    this.material.uniforms.time.value = this.time;

    if (isIe()) {
      const curItem = videos[this.curslide];
      if (curItem) renderVideo(curItem);
    }

    this.renderer.render(this.scene, this.camera);
  }

  getIndices() {
    const lastItem = this.gallery.length - 1;
    const probNext = this.activeSlide + 1;
    const probPrev = this.activeSlide - 1;
    const next = probNext > lastItem ? 0 : probNext;
    const prev = probPrev < 0 ? lastItem : probPrev;
    return { prev, next };
  }

  play() {
    if (this.gallery.length < 2) {
      return;
    }
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.moveSlider({ direction: 'next' });
    }, 10000);
  }

  start() {
    if (DOCUMENT_ELEMENT.classList.contains('has-preloader')) {
      DOCUMENT_ELEMENT.addEventListener('preloaderEnd', () => {
        setTimeout(this.showButton.bind(this), 700);
        this.start();
      });
    } else {
      this.playing = true;
      this.element.removeEventListener('slideChanged', this.stop, { once: true });
      this.play();
      playVideo(this.gallery[this.curslide].image);
      TweenMax.ticker.removeEventListener('tick', this.render);
      TweenMax.ticker.addEventListener('tick', this.render);
    }
  }

  stop() {
    if (this.slideChaging) {
      this.element.addEventListener('slideChanged', this.stop, { once: true });
    } else {
      this.playing = false;
      stopVideo(this.gallery[this.curslide].image);
      clearTimeout(this.timeout);
      TweenMax.ticker.removeEventListener('tick', this.render);
    }
  }

  showButton() {
    const { $button } = this.gallery[this.curslide];
    if ($button) {
      $button.addClass('is-active');
    }
  }

  hideButton() {
    const { $button } = this.gallery[this.activeSlide];
    if ($button) {
      $button.removeClass('is-active');
    }
  }
}

const coverSliderInitializer = props => new Promise(async (resolve) => {
  const options = Object.assign({}, defaults, props);
  if (!options.element) {
    resolve(true);
    return;
  }
  const slides = Array.from(options.element.querySelectorAll(options.slide)).map((slide) => {
    const {
      type, src, hasButton, buttonParams,
    } = slide.dataset;
    return {
      src, type, hasButton, buttonParams,
    };
  });
  if (!slides.length) {
    resolve(true);
    return;
  }
  const gallery = await Promise.all(loadImages(slides));
  if (gallery.find(el => el.err)) {
    // eslint-disable-next-line no-console
    console.warn('There is no images for Cover Banner');
    resolve(true);
    return;
  }
  if (gallery) {
    options.gallery = gallery;
    resolve(new CoverSlider(options));
  }
});

export default coverSliderInitializer;



// WEBPACK FOOTER //
// ./scripts/elements/CoverSlider/CoverSlider.js